/**
 * Audit-Log view model — pure derivations over `AuditLogResponse`.
 *
 * The server reconstructs the chronicle; this module only decides how each
 * step reads: a headline, the conclusion it stands for ("Schluss"), a tone,
 * the filter group and the day it belongs to. Nothing here does I/O.
 */

import type { AuditCase, AuditEntry, AuditKind, Bewertung } from '../../lib/types';
import { BEWERTUNG_LABELS } from '../triage/eventHelpers';

export type AuditTone = 'neutral' | 'good' | 'warn' | 'alert' | 'info';

/** Filter groups offered above the timeline. */
export type AuditGroup = 'alle' | 'bewertungen' | 'meldungen' | 'nachweise' | 'system';

export const AUDIT_GROUPS: readonly { key: AuditGroup; label: string }[] = [
  { key: 'alle', label: 'Alle' },
  { key: 'bewertungen', label: 'Bewertungen' },
  { key: 'meldungen', label: 'Kundenmeldungen' },
  { key: 'nachweise', label: 'Nachweise' },
  { key: 'system', label: 'System' },
];

const GROUP_OF: Readonly<Record<AuditKind, Exclude<AuditGroup, 'alle'>>> = {
  bewertung: 'bewertungen',
  meldung: 'meldungen',
  versand: 'meldungen',
  advisory: 'nachweise',
  nachweis: 'nachweise',
  erkannt: 'system',
  behoben: 'system',
  wiedereroeffnet: 'system',
};

export const KIND_LABELS: Readonly<Record<AuditKind, string>> = {
  erkannt: 'Erkennung',
  bewertung: 'Bewertung',
  wiedereroeffnet: 'Wiedereröffnung',
  behoben: 'Automatischer Abschluss',
  meldung: 'Kundenmeldung',
  versand: 'Versand',
  advisory: 'Advisory',
  nachweis: 'Nachweis',
};

export function groupOf(entry: AuditEntry): Exclude<AuditGroup, 'alle'> {
  return GROUP_OF[entry.kind];
}

const MANUAL: ReadonlySet<Bewertung> = new Set(['BETROFFEN', 'NICHT_BETROFFEN']);

/** A decided verdict replaced by a different one. */
export function isReassessment(entry: AuditEntry): boolean {
  return (
    entry.kind === 'bewertung' &&
    entry.vorher !== null &&
    MANUAL.has(entry.vorher) &&
    entry.vorher !== entry.bewertung
  );
}

export function verdictLabel(value: Bewertung | null): string {
  return value === null ? 'Offen' : BEWERTUNG_LABELS[value];
}

export function toneOf(entry: AuditEntry): AuditTone {
  switch (entry.kind) {
    case 'bewertung':
      return entry.bewertung === 'BETROFFEN' ? 'alert' : 'good';
    case 'behoben':
      return 'good';
    case 'wiedereroeffnet':
      return 'warn';
    case 'versand':
      return entry.fristgerecht === false ? 'warn' : 'good';
    case 'meldung':
      return 'info';
    default:
      return 'neutral';
  }
}

function komponenteLabel(entry: AuditEntry | AuditCase): string {
  const { name, version } = entry.komponente;
  if (!name) return 'Komponente';
  return version ? `${name} ${version}` : name;
}

export { komponenteLabel };

/** Headline of one step, in German, sentence case. */
export function titleOf(entry: AuditEntry): string {
  const cve = entry.cve_id ?? 'CVE';
  switch (entry.kind) {
    case 'erkannt':
      return `KEV-Treffer ${cve} in ${komponenteLabel(entry)} erkannt`;
    case 'bewertung':
      if (isReassessment(entry)) {
        return `${cve} neu bewertet: ${verdictLabel(entry.bewertung).toLowerCase()}`;
      }
      if (entry.vorher === entry.bewertung) return `Bewertung von ${cve} bestätigt`;
      return entry.bewertung === 'BETROFFEN'
        ? `${cve} als betroffen bewertet`
        : `${cve} als nicht betroffen bewertet`;
    case 'wiedereroeffnet':
      return `${cve} automatisch wieder geöffnet`;
    case 'behoben':
      return `${cve} automatisch als behoben geschlossen`;
    case 'meldung': {
      const count = entry.kunden.length;
      return count === 1
        ? `Kundenmeldung für ${entry.kunden[0]} angelegt`
        : `${count} Kundenmeldungen angelegt`;
    }
    case 'versand':
      return entry.kunden.length > 0
        ? `${entry.kunden.join(', ')} benachrichtigt`
        : 'Kundenbenachrichtigung versendet';
    case 'advisory':
      return `Advisory-Entwurf zu ${cve} gespeichert`;
    case 'nachweis':
      return `Nachweis zu ${cve} hochgeladen`;
  }
}

/** The conclusion a step stands for — null when it records no conclusion. */
export function conclusionOf(entry: AuditEntry): string | null {
  switch (entry.kind) {
    case 'bewertung':
      return entry.bewertung === 'BETROFFEN'
        ? 'Anwendbar in der ausgelieferten Konfiguration. Betroffene Kunden sind zu informieren.'
        : 'Nicht anwendbar in der ausgelieferten Konfiguration. Keine Kundenmeldung erforderlich.';
    case 'behoben':
      return 'Keine ausgelieferte Konfiguration ist mehr betroffen. Der Fall ist geschlossen.';
    case 'wiedereroeffnet':
      return 'Der CVE wird erneut gemeldet. Die Anwendbarkeit ist neu zu bewerten.';
    default:
      return null;
  }
}

export function matchesAuditSearch(entry: AuditEntry, needle: string): boolean {
  const query = needle.trim().toLowerCase();
  if (query === '') return true;
  return [
    entry.cve_id,
    entry.komponente.name,
    entry.komponente.hersteller,
    entry.actor,
    entry.begruendung,
    entry.detail,
    ...entry.kunden,
  ].some((value) => value !== null && value !== undefined && value.toLowerCase().includes(query));
}

export function matchesCaseSearch(entry: AuditCase, needle: string): boolean {
  const query = needle.trim().toLowerCase();
  if (query === '') return true;
  return [
    entry.cve_id,
    entry.komponente.name,
    entry.komponente.hersteller,
    entry.bewertet_von,
    entry.begruendung,
  ].some((value) => value !== null && value.toLowerCase().includes(query));
}

/* --- Dates, durations, people ------------------------------------------- */

function dayKey(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

export function dayLabel(iso: string | null, now = new Date()): string {
  if (iso === null) return 'Ohne Zeitstempel';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'Ohne Zeitstempel';
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (dayKey(date) === dayKey(now)) return 'Heute';
  if (dayKey(date) === dayKey(yesterday)) return 'Gestern';
  return date.toLocaleDateString('de-DE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function timeLabel(iso: string | null): string {
  if (iso === null) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

/** Entries grouped per calendar day, keeping the newest-first order. */
export function groupByDay(
  entries: readonly AuditEntry[],
): { key: string; label: string; entries: AuditEntry[] }[] {
  const groups: { key: string; label: string; entries: AuditEntry[] }[] = [];
  for (const entry of entries) {
    const label = dayLabel(entry.ts);
    const last = groups[groups.length - 1];
    if (last !== undefined && last.label === label) last.entries.push(entry);
    else groups.push({ key: `${label}-${groups.length}`, label, entries: [entry] });
  }
  return groups;
}

/** '< 1 h', '4 h', '1,5 Tage'. */
export function fmtDuration(hours: number | null): string {
  if (hours === null) return '—';
  if (hours < 1) return '< 1 h';
  if (hours < 48) return `${Math.round(hours)} h`;
  const days = hours / 24;
  return `${days.toLocaleString('de-DE', { maximumFractionDigits: 1 })} Tage`;
}

export function initials(name: string): string {
  const parts = name
    .replace(/[^\p{L}\p{N}\s-]/gu, ' ')
    .split(/[\s-]+/)
    .filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : (parts[0]?.[1] ?? '');
  return `${first}${last}`.toUpperCase();
}

export function percent(part: number, whole: number): number | null {
  return whole > 0 ? Math.round((part / whole) * 100) : null;
}

/* --- Case file stepper -------------------------------------------------- */

export type StepState = 'done' | 'current' | 'warn' | 'skipped' | 'pending';

export interface CaseStep {
  label: string;
  state: StepState;
  hint: string;
}

export function caseSteps(entry: AuditCase): CaseStep[] {
  const decided = entry.bewertung === 'BETROFFEN' || entry.bewertung === 'NICHT_BETROFFEN';
  const { gesamt, versendet, ueberfaellig } = entry.meldungen;
  const steps: CaseStep[] = [
    { label: 'Erkannt', state: 'done', hint: 'KEV-Scan' },
  ];

  if (entry.bewertung === 'OFFEN') {
    steps.push({ label: 'Bewertet', state: 'current', hint: 'Entscheidung fällig' });
  } else if (entry.bewertung === 'BEHOBEN' && entry.entscheidungen === 0) {
    steps.push({ label: 'Bewertet', state: 'skipped', hint: 'nicht nötig' });
  } else {
    steps.push({
      label: 'Bewertet',
      state: 'done',
      hint: entry.letzter_bewerter ?? entry.bewertet_von ?? 'erfasst',
    });
  }

  if (entry.bewertung === 'BETROFFEN') {
    steps.push({
      label: 'Gemeldet',
      state: gesamt > 0 ? 'done' : 'current',
      hint: gesamt > 0 ? `${gesamt} Kunde(n)` : 'Meldung ausstehend',
    });
    const allSent = gesamt > 0 && versendet >= gesamt;
    steps.push({
      label: 'Informiert',
      state: allSent ? 'done' : ueberfaellig > 0 ? 'warn' : gesamt > 0 ? 'current' : 'pending',
      hint: gesamt > 0 ? `${versendet} / ${gesamt} versendet` : 'ausstehend',
    });
  } else {
    const closed = decided || entry.bewertung === 'BEHOBEN';
    steps.push({ label: 'Gemeldet', state: closed ? 'skipped' : 'pending', hint: closed ? 'entfällt' : '—' });
    steps.push({
      label: 'Abgeschlossen',
      state: closed ? 'done' : 'pending',
      hint: entry.bewertung === 'BEHOBEN' ? 'automatisch' : closed ? 'ohne Meldepflicht' : '—',
    });
  }
  return steps;
}

/* --- Export ------------------------------------------------------------- */

function csvCell(value: string | number | null | undefined): string {
  const text = value === null || value === undefined ? '' : String(value);
  return /[";\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/** Semicolon CSV with BOM, so German Excel opens umlauts and columns correctly. */
export function toCsv(entries: readonly AuditEntry[]): string {
  const header = [
    'Zeitpunkt (UTC)',
    'Art',
    'Akteur',
    'CVE',
    'Komponente',
    'Vorher',
    'Bewertung',
    'Schluss',
    'Begründung',
    'Kunden',
    'Detail',
    'Frist 24 h',
    'Fristgerecht',
  ];
  const rows = entries.map((entry) => [
    entry.ts,
    KIND_LABELS[entry.kind],
    entry.actor,
    entry.cve_id,
    komponenteLabel(entry),
    entry.kind === 'bewertung' ? verdictLabel(entry.vorher) : '',
    entry.bewertung === null ? '' : verdictLabel(entry.bewertung),
    conclusionOf(entry),
    entry.begruendung,
    entry.kunden.join(', '),
    entry.detail,
    entry.frist,
    entry.fristgerecht === null ? '' : entry.fristgerecht ? 'ja' : 'nein',
  ]);
  return `﻿${[header, ...rows].map((row) => row.map(csvCell).join(';')).join('\r\n')}\r\n`;
}
